import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useTransferEligibility } from "@/features/salesforceIntegration/hooks/useTransferEligibility";
import { useSalesforceDataTransfer } from "@/features/salesforceIntegration/hooks/useSalesforceDataTransfer";
import {
  summarizeTransferSelection,
  type TransferSelectionSummary,
} from "@/features/salesforceIntegration/utils/summarizeTransferSelection";
import {
  resolveTransferActionState,
  type TransferActionState,
} from "@/features/salesforceIntegration/utils/resolveTransferActionState";
import {
  type TransferEligibilityRealEstate,
  type TransferStatus,
} from "@/features/salesforceIntegration/types";

/** 実行中でないときに返す空集合。毎レンダーで生成して再描画を誘発しないよう固定する。 */
const EMPTY_UNIQUE_IDS: ReadonlySet<string> = new Set();

/** Salesforce連携が無効なときに返す空の値。生成そのものを省くために固定する。 */
const EMPTY_REAL_ESTATE_MAP: ReadonlyMap<
  string,
  TransferEligibilityRealEstate
> = new Map();
const EMPTY_SUMMARY: TransferSelectionSummary = {
  selectedRowCount: 0,
  targetUniqueIds: [],
  excludedReasons: [],
};

/**
 * ジョブの決着後に判定APIを取り直す進み具合。
 * `refreshing`は取り直し中、`done`は取り直しを試み終えた状態を表す。
 */
type EligibilityRefreshPhase = "refreshing" | "done";

/** FeedGridの行のうち、この hook が必要とする形だけを受け取る。 */
export interface RowWithUniqueIds {
  id: number;
  uniqueIds: string[];
}

export interface UseSalesforceFeedGridIntegrationParams {
  /**
   * Salesforce連携を有効にするか。機能フラグと契約の判定は画面側（`useSalesforceTransferGate`）で
   * 行い、ここへは結果だけを渡す。無効なら判定APIもポーリングも走らせない。
   */
  isEnabled: boolean;
  /** 対象リクエスト。ルートパラメータが無い場合は`undefined`。 */
  pictureRequestId: number | undefined;
  /** FeedGridの全行（フィルタ非適用）。 */
  convertedRows: readonly RowWithUniqueIds[];
  /** DataGridの選択モデル。 */
  selectedRowIds: ReadonlyArray<number | string>;
  isReadOnly: boolean;
}

export interface UseSalesforceFeedGridIntegrationResult {
  /** Salesforce列が行の状態を引くための辞書。 */
  realEstateByUniqueId: ReadonlyMap<string, TransferEligibilityRealEstate>;
  /**
   * 実行中の連携ジョブへ送った物件。送った行だけを「連携中」にするために使う。
   * 判定APIを取り直し終えるまで保持する。
   */
  inFlightUniqueIds: ReadonlySet<string>;
  /** 操作メニュー項目の活性とツールチップ。 */
  actionState: TransferActionState;
  /** 送信対象と除外の内訳。確認ダイアログに渡す。 */
  summary: TransferSelectionSummary;
  maxTransferableCount: number | undefined;
  isConfirmDialogOpen: boolean;
  openConfirmDialog: () => void;
  closeConfirmDialog: () => void;
  /** 確認ダイアログの「連携する」。送信して閉じる。 */
  confirmTransfer: () => void;
  /** 直近の連携ジョブの状態。失敗の通知に使う。 */
  transferStatus: TransferStatus | null;
  transferErrorMessage: string | null;
  /** 判定APIの取り直しに失敗し、操作を止めている状態か。 */
  hasEligibilityRefreshFailed: boolean;
  /** 取り直しの再試行。失敗時の復帰経路として画面から呼ぶ。 */
  retryEligibilityRefresh: () => void;
}

/**
 * 取得済不動産登記の詳細画面で、FeedGridの選択とSalesforce連携をつなぐ hook。
 *
 * 判定APIの取得・送信対象の算出・メニュー項目の活性判定を1か所に集約する。
 * 参謀くん連携で判定条件が5か所に散り、解析中でもボタンが活性になった不具合を
 * 繰り返さないためである（実装計画書7.4節）。
 *
 * 画面内で`/acquirebookstatus/:id`を切り替えてもこのhookは作り直されない。
 * そのため保持する状態はすべて依頼IDと対で持ち、別の依頼を見ている間は使わない。
 * useEffectで消す方式にしないのは、遷移後に遅れて届いた応答を取りこぼすためである。
 */
export const useSalesforceFeedGridIntegration = (
  params: UseSalesforceFeedGridIntegrationParams
): UseSalesforceFeedGridIntegrationResult => {
  const {
    isEnabled: isFeatureEnabled,
    pictureRequestId,
    convertedRows,
    selectedRowIds,
    isReadOnly,
  } = params;

  const [confirmDialogRequestId, setConfirmDialogRequestId] = useState<
    number | null
  >(null);
  // 判定APIは実行中をリクエスト単位でしか返さないため、
  // 送った行だけを「連携中」にするには画面側で覚えておくしかない。
  // 依頼IDごとに持つのは、依頼をまたいで送ったときに互いを上書きしないため
  const [submittedByRequest, setSubmittedByRequest] = useState<
    Readonly<Record<number, ReadonlySet<string> | undefined>>
  >({});
  const [refreshPhaseByRequest, setRefreshPhaseByRequest] = useState<
    Readonly<Record<number, EligibilityRefreshPhase | undefined>>
  >({});

  // 無効なら空のIDリストを渡し、判定APIを呼ばせない（hook自体は必ず呼ぶ）
  const isEnabled = isFeatureEnabled && pictureRequestId !== undefined;
  const {
    results,
    error: eligibilityError,
    mutate,
  } = useTransferEligibility(isEnabled ? [pictureRequestId] : [], true);
  const eligibility = results[0];

  const refreshEligibility = useCallback(
    (targetPictureRequestId: number) => {
      setRefreshPhaseByRequest((previous) => ({
        ...previous,
        [targetPictureRequestId]: "refreshing",
      }));
      // `mutate()`は再検証に失敗しても解決するため、成否は`eligibilityError`で見る。
      // ここでは「取り直しを試み終えた」ことだけを記録する
      const markDone = (): void => {
        setRefreshPhaseByRequest((previous) =>
          previous[targetPictureRequestId] === "refreshing"
            ? { ...previous, [targetPictureRequestId]: "done" }
            : previous
        );
      };
      mutate().then(markDone).catch(markDone);
    },
    [mutate]
  );

  const { status, errorMessage, isTransferring, transfer } =
    useSalesforceDataTransfer({
      isEnabled,
      pictureRequestId: pictureRequestId ?? 0,
      latestTransfer: eligibility?.latestTransfer ?? null,
      onSettled: () => {
        if (pictureRequestId === undefined) return;
        refreshEligibility(pictureRequestId);
      },
    });

  // 別の依頼を見ている間は、前の依頼で覚えた値を使わない
  const submittedUniqueIds =
    pictureRequestId === undefined
      ? EMPTY_UNIQUE_IDS
      : submittedByRequest[pictureRequestId] ?? EMPTY_UNIQUE_IDS;
  const refreshPhase =
    pictureRequestId === undefined
      ? undefined
      : refreshPhaseByRequest[pictureRequestId];
  const isAwaitingEligibilityRefresh = refreshPhase === "refreshing";
  // 取り直しを試み終えたのに直近の取得が失敗している＝手元は連携前の古い結果のまま
  const hasEligibilityRefreshFailed =
    refreshPhase === "done" && eligibilityError !== undefined;

  // 無効なら辞書も内訳も作らない。明細は数万件になりうるため、
  // 選択が変わるたびに全行を走査する処理を残さない
  const realEstateByUniqueId = useMemo(
    () =>
      isEnabled
        ? new Map(
            (eligibility?.realEstates ?? []).map((realEstate) => [
              realEstate.uniqueId,
              realEstate,
            ])
          )
        : EMPTY_REAL_ESTATE_MAP,
    [isEnabled, eligibility]
  );

  const summary = useMemo(() => {
    if (!isEnabled) return EMPTY_SUMMARY;
    const selectedIds = new Set(selectedRowIds);
    const selectedRows = convertedRows.filter((row) => selectedIds.has(row.id));
    return summarizeTransferSelection(selectedRows, realEstateByUniqueId);
  }, [isEnabled, convertedRows, selectedRowIds, realEstateByUniqueId]);

  const actionState = resolveTransferActionState({
    isReadOnly,
    eligibility,
    isTransferring,
    isAwaitingEligibilityRefresh,
    hasEligibilityRefreshFailed,
    selectedRowCount: summary.selectedRowCount,
    targetCount: summary.targetUniqueIds.length,
    maxTransferableCount: eligibility?.maxTransferableCount,
  });

  // 取り直しが終わるまでは判定結果が連携前のままなので、送った行を「連携可」へ戻さない
  const keepsSubmittedRows =
    isTransferring ||
    isAwaitingEligibilityRefresh ||
    hasEligibilityRefreshFailed;

  return {
    realEstateByUniqueId,
    inFlightUniqueIds: keepsSubmittedRows
      ? submittedUniqueIds
      : EMPTY_UNIQUE_IDS,
    actionState,
    summary,
    maxTransferableCount: eligibility?.maxTransferableCount,
    isConfirmDialogOpen:
      confirmDialogRequestId !== null &&
      confirmDialogRequestId === pictureRequestId,
    openConfirmDialog: () => {
      if (pictureRequestId === undefined) return;
      setConfirmDialogRequestId(pictureRequestId);
    },
    closeConfirmDialog: () => {
      setConfirmDialogRequestId(null);
    },
    confirmTransfer: () => {
      // ダイアログを開いている間に判定結果が変わることがある。
      // 送れない状態になっていたら送らない（対象が全部除外されると空配列を送ってしまう）
      if (actionState.isDisabled || pictureRequestId === undefined) return;

      setConfirmDialogRequestId(null);
      const requestedFor = pictureRequestId;
      const targetUniqueIds = summary.targetUniqueIds;
      setSubmittedByRequest((previous) => ({
        ...previous,
        [requestedFor]: new Set(targetUniqueIds),
      }));
      transfer(targetUniqueIds).catch(() => {
        setSubmittedByRequest((previous) => {
          const { [requestedFor]: _removed, ...rest } = previous;
          return rest;
        });
        toast.error("Salesforceへの連携に失敗しました");
      });
    },
    transferStatus: status,
    transferErrorMessage: errorMessage,
    hasEligibilityRefreshFailed,
    retryEligibilityRefresh: () => {
      if (pictureRequestId === undefined) return;
      refreshEligibility(pictureRequestId);
    },
  };
};
